// attendanceApi.ts
import api from '../api/api';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { SSIDPlugin } from 'capacitor-ssid-plugin';

export interface AttendanceSession {
  in_time: string | null;
  out_time: string | null;
  // The AttendanceRecord row this punch came from. Send it back as `record_id`
  // when editing so the change lands on THIS punch — a day can hold several,
  // and without it the backend edits the day's most recent row instead.
  record_id?: string | null;
  out_record_id?: string | null;
  // This punch's own place. A day can mix an office punch with a WFH one, so
  // it is per-session and not just per-day.
  location?: "in_office" | "work_from_home" | null;
  // Which Teamlio client the punch was made from. Null on rows written before
  // the backend started recording it — render a dash, never a guess.
  attendance_mode?: AttendanceMode | null;
}

// The four front doors a punch can arrive through, plus the marker the backend
// puts on rows an admin entered on someone else's behalf (mark present / mark
// absent / edited hours) — those are not punches and must not read as one.
export type AttendanceMode =
  | "website"
  | "mobile_app"
  | "messenger_app"
  | "workflow"
  | "manual";

export const ATTENDANCE_MODE_LABELS: Record<AttendanceMode, string> = {
  website: "Website",
  mobile_app: "Mobile App",
  messenger_app: "Messenger",
  workflow: "Workflow",
  manual: "Manual",
};

// What THIS build is. The same bundle ships as the browser app and, wrapped by
// Capacitor, as the Android app — so the running platform is the only honest
// answer, and it is one the server cannot reliably derive from a request.
export const clientAttendanceMode = (): AttendanceMode =>
  Capacitor.isNativePlatform() ? "mobile_app" : "website";

export interface AttendanceWorkingHour {
  date: string;
  shift_duration: string;
  In_time: string;
  out_time: string;
  total_working_hours: string;
  total_working_hours_words: string;
  total_hours: number;
  // Additive (remote employees): every clock-in / clock-out pair for the
  // day. The first_in / last_out fields above stay as-is for all modes.
  sessions?: AttendanceSession[];
  // The employee's configured working mode (in-office / remote / hybrid). The
  // UI renders the In Office / WFH badge from this.
  mode_of_working?: string | null;
  attendance_location?: "in_office" | "work_from_home" | null;
  // Overtime employees get the same per-day punch breakdown as remote.
  is_overtime?: boolean;
  // Approved-leave overlay: true when this day falls inside an approved
  // leave range. Days with no clock-in render as "Leave" instead of
  // "Absent". leave_type is the LeaveDetails.leaveType when known.
  on_leave?: boolean;
  leave_type?: string | null;
  // Present even without clock times (e.g. admin "mark present" days).
  present?: boolean;
  // Viewed employee's joining date (YYYY-MM-DD). Days before it render as N/A.
  hire_date?: string | null;
}

export interface AttendanceStatus {
  name: string;
  // "Present" | "Absent" | "Leave" | "Holiday" | "N/A". An employee on
  // approved leave for the queried date (and not clocked in) comes back as
  // "Leave"; on a declared company holiday, un-punched rows come back as
  // "Holiday" instead of "Absent".
  status: string;
  // Holiday name (from the branch holiday calendar) when status = "Holiday".
  holiday_name?: string | null;
  from_date?: string;
  clock_in_time?: string;
  // First clock-in / final clock-out for the day. out_time is null while the
  // person is still clocked in (a clock-in dated after their last clock-out).
  out_time?: string | null;
  user_id?: string;
  // Human-friendly employee code (Employee.employeeID), shown under the name.
  employeeID?: string | null;
  leave_type?: string | null;
  // The employee's configured working mode (in-office / remote / hybrid). The
  // UI renders the In Office / WFH badge from this.
  mode_of_working?: string | null;
  attendance_location?: "in_office" | "work_from_home" | null;
  // Every clock-in / clock-out pair for the day (>1 means multiple punches).
  sessions?: AttendanceSession[];
  // Employee's joining date (YYYY-MM-DD). The per-employee Attendance Details
  // modal uses it to show days before joining as N/A (not Absent) and to
  // disable their Edit action.
  hire_date?: string | null;
}

export interface TodayAttendance {
  name: string;
  date: string;
  clock_in_time?: string;
  additional_in_time?: string;
  additional_out_time?: string;
  end_time?: string;
  total_time?: string;
}

export interface PieData {
  name: string;
  value: number;
}

// Get attendance day-wise working hours for an employee
export const getAttendanceDayWise = async (userId: string, startDate: string, endDate: string) => {
  try {
    const response = await api.get(`/attendance_daywise_working_hours`, {
      params: {
        user_id: userId,
        start_date: startDate,
        end_date: endDate
      }
    });
    return response.data?.data || [];
  } catch (error: any) {
    console.error('Error fetching attendance day-wise:', error);
    // FastAPI uses `detail` for HTTPException bodies (e.g. "Office
    // hours not found"); fall back to `.error` / `.message` for older
    // shapes. Throwing the real backend reason lets the UI surface it.
    const msg =
      error?.response?.data?.detail ||
      error?.response?.data?.error ||
      error.message;
    throw new Error(msg);
  }
};

// Get attendance summary
export const getAttendanceSummary = async (startDate: string, endDate: string) => {
  try {
    const response = await api.get(`/attendance_summary`, {
      params: {
        start_date: startDate,
        end_date: endDate
      }
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching attendance summary:', error);
    throw new Error(error?.response?.data?.error || error.message);
  }
};

// Get all attendance records for a single date (branch-scoped).
// Returns an array of { id, user_id, date, clock_in_time, clock_out_time, status }.
// Used by the dashboard to build a real day-by-day presence trend, since
// /attendance_summary only returns status totals (no per-day breakdown).
export const getAttendanceByDate = async (
  attendanceDate: string,
  // By default a failure returns [] so the dashboard's many per-day calls
  // degrade to an empty bar. Pass false where "" and "couldn't tell" mean
  // DIFFERENT things — the clock buttons read an empty list as "not clocked
  // in", which would offer Clock In to someone who already has a punch open.
  swallowErrors = true,
) => {
  try {
    const response = await api.get(`/attendanceAll`, {
      params: { date: attendanceDate },
    });
    return Array.isArray(response.data) ? response.data : (response.data?.data || []);
  } catch (error: any) {
    console.error('Error fetching attendance for date:', attendanceDate, error);
    if (!swallowErrors) throw error;
    return [];
  }
};

// Org daily-presence for a date range (branch-scoped). Returns the active
// headcount plus, per day, the number of DISTINCT present employees and the
// percentage present. Powers the admin dashboard's Monthly Presence Calendar in
// ONE call (instead of 30+ /attendanceAll calls).
export interface DailyPresence {
  total_employees: number;
  start_date: string;
  end_date: string;
  days: { date: string; present: number; percent: number }[];
}

export const getDailyPresence = async (
  startDate: string,
  endDate: string,
): Promise<DailyPresence> => {
  const response = await api.get(`/attendance_daily_presence`, {
    params: { start_date: startDate, end_date: endDate },
  });
  return response.data;
};

// Top employees by total worked hours over a range (branch-scoped). Powers the
// admin dashboard's "Top Active Employees this month" chart. Hours are derived
// from clock-in/out pairs server-side (never stored), matching the numbers the
// day-wise attendance modal shows. Employees with no worked time are INCLUDED
// (0h, sorted last) — fewer than `limit` rows just means a small branch.
export interface TopActiveEmployee {
  employee_id: string;
  user_id: string | null;
  name: string;
  employeeID: string | null;
  total_seconds: number;
  total_hours: number;        // e.g. 30.33
  total_hours_label: string;  // e.g. "30:20"
}

export interface TopActiveEmployees {
  start_date: string;
  end_date: string;
  employees: TopActiveEmployee[];
}

export const getTopActiveEmployees = async (
  startDate: string,
  endDate: string,
  limit = 5,
): Promise<TopActiveEmployees> => {
  const response = await api.get(`/attendance_top_active_employees`, {
    params: { start_date: startDate, end_date: endDate, limit },
  });
  return response.data;
};

// Get attendance status for a specific date
export const getAttendanceStatus = async (attendanceDate: string) => {
  try {
    const response = await api.get(`/attendance_status`, {
      params: {
        attendance_date: attendanceDate
      }
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching attendance status:', error);
    throw new Error(error?.response?.data?.error || error.message);
  }
};

// ----- Web Clock In / Clock Out -----
// The mobile app uses Capacitor's Geolocation + a native geofence worker
// to auto-clock. The web build needs a manual equivalent: ask the browser
// for the user's coordinates, gate them against the configured office
// geofences (same data the Android `GeofenceManager` reads), and POST to
// the same `/attendance` endpoint the mobile manual clock-out uses.

// Haversine distance in meters. Used to mirror the radius check the
// Android side does with Geofence.setCircularRegion.
const haversineMeters = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
) => {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

// A fix we'd happily geofence against. Anything coarser is a network / IP
// derived guess that can sit hundreds of metres from the real position.
const GOOD_ENOUGH_ACCURACY_M = 60;
// Worse than this and the reading cannot have come from GPS or a Wi-Fi scan —
// it is the browser's IP-address fallback (or a single cell tower), which
// resolves to the ISP's exchange and is routinely kilometres from the desk the
// person is sitting at. A desktop wired to ETHERNET is the classic case: no GPS
// chip, and no Wi-Fi adapter scanning nearby access points, so the only signal
// left is the public IP. The coordinates still look perfectly ordinary, which
// is why geofencing them produced a confident and completely wrong
// "you're 3.4 km from the office".
const COARSE_ACCURACY_M = 500;
// At or beyond this there is no satellite / Wi-Fi input in the mix at all, only
// the IP lookup, and waiting longer cannot improve it. Between COARSE and this
// (a cell-tower fix on a phone) a real GPS lock usually does still arrive — it
// is just slower indoors than our normal settle window.
const NETWORK_ONLY_ACCURACY_M = 5000;
// How long we keep watching for a better fix before going with the best we got.
const LOCATION_SETTLE_MS = 8000;
// Extended deadline used only when the settle window expired with nothing but a
// cell-tower-grade fix. Indoors a phone routinely needs ~15s for GPS to
// overtake the tower estimate it hands out first; failing at 8s on a reading we
// already know is un-geofenceable just guarantees a pointless error.
const LOCATION_MAX_WAIT_MS = 20000;
// A fix this fresh can be reused instead of forcing a brand-new GPS lock.
// Nobody teleports between one punch and the next a minute later, and a cold
// lock is the single biggest cost in this function (measured ~9s to settle
// indoors) — reusing a recent one skips it entirely on back-to-back punches
// (e.g. a retry, or clock-in immediately followed by clock-out).
const LOCATION_CACHE_MS = 120_000;

export type BrowserFix = { latitude: number; longitude: number; accuracy: number };

// Browsers only hand out location on a secure origin. Reaching the app over the
// office LAN by IP (http://192.168.1.20:3000) is NOT one — and Chrome reports
// that as PERMISSION_DENIED, which sends people digging through browser
// settings for a permission that was never the problem. localhost is exempt.
export const geolocationBlockedByInsecureOrigin = (): boolean => {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext) return false;
  const host = window.location.hostname;
  return !(host === "localhost" || host === "127.0.0.1" || host === "[::1]");
};

// NOTE: the wording for an untrustworthy fix deliberately lives ONLY on the
// server (`geofence_blocker_message`). It is the side that knows whether the
// office-IP fallback rescued the punch, so it is the side that gets to say why
// one was refused — a second copy here would drift out of step with it.

// Wrap navigator.geolocation in a promise with friendly error messages.
// We use the raw browser API (not @capacitor/geolocation) because this
// function is web-only and the messages should reflect the browser
// permission flow the user actually sees.
//
// This WATCHES rather than taking a single reading. The very first fix a
// browser hands out is routinely the cached network estimate — accurate to a
// kilometre or worse — which is why clocking in used to fail the first time and
// work after a re-login (by then the device had a warm GPS fix). We keep the
// most accurate reading seen and return as soon as one is good enough.
// Exported for the face check-in path, which posts its own attendance payload
// and needs the SAME fix this one does. Without a location the server's
// geofence refuses the punch — and the face having verified says nothing about
// where the person is standing.
export const getBrowserCoordinates = (): Promise<BrowserFix> =>
  new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      reject(new Error("Geolocation isn't supported by this browser."));
      return;
    }
    if (geolocationBlockedByInsecureOrigin()) {
      reject(
        new Error(
          "This page is open over plain HTTP, so the browser refuses to share your location. Open the app over its https:// address (or ask IT for the secure link) and try again.",
        ),
      );
      return;
    }

    let best: BrowserFix | null = null;
    let watchId: number | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let settled = false;

    const stop = () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (timer !== null) clearTimeout(timer);
      watchId = null;
      timer = null;
    };
    const succeed = () => {
      if (settled || !best) return;
      settled = true;
      stop();
      resolve(best);
    };
    const fail = (err: GeolocationPositionError) => {
      // A late error after we already have a usable fix is not a failure.
      if (settled) return;
      if (best) {
        succeed();
        return;
      }
      settled = true;
      stop();
      if (err.code === err.PERMISSION_DENIED) {
        reject(
          new Error(
            "Location permission denied. Allow location access in your browser to clock in/out.",
          ),
        );
      } else if (err.code === err.POSITION_UNAVAILABLE) {
        // The usual cause on a desk PC is not a weak signal but no location
        // source at all: wired to Ethernet (nothing to scan) and/or the OS
        // location service switched off, which Windows reports as this code.
        reject(
          new Error(
            "Your device couldn't determine a location. On a desktop, turn Wi-Fi on (it doesn't need to connect to anything) and switch on Settings → Privacy & security → Location. On a phone, enable location and move near a window. You can also clock in from your phone.",
          ),
        );
      } else if (err.code === err.TIMEOUT) {
        reject(new Error("Location request timed out. Please try again."));
      } else {
        reject(new Error(err.message || "Could not get your location."));
      }
    };

    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const fix: BrowserFix = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : Number.MAX_SAFE_INTEGER,
        };
        if (!best || fix.accuracy < best.accuracy) best = fix;
        if (best.accuracy <= GOOD_ENOUGH_ACCURACY_M) succeed();
      },
      fail,
      { enableHighAccuracy: true, timeout: LOCATION_MAX_WAIT_MS + 4000, maximumAge: LOCATION_CACHE_MS },
    );

    // Time's up — use the best reading we managed to collect. Two deadlines:
    // the normal one, and an extension for the one case where waiting still
    // pays off (a phone that has so far only offered a cell-tower fix). A
    // reading in the IP-only band never improves, so that one gives up on time
    // rather than making the user stare at a spinner for another 12s.
    const onDeadline = (extended: boolean) => () => {
      if (settled) return;
      const worthWaitingLonger =
        // Nothing at all yet: a cold GPS lock can stay silent past 8s, and the
        // failure modes that produce no fix EVER (permission, no provider)
        // arrive as errors through fail(), not as silence.
        !best ||
        (best.accuracy > COARSE_ACCURACY_M && best.accuracy < NETWORK_ONLY_ACCURACY_M);
      if (!extended && worthWaitingLonger) {
        timer = setTimeout(onDeadline(true), LOCATION_MAX_WAIT_MS - LOCATION_SETTLE_MS);
        return;
      }
      if (best) succeed();
      else {
        settled = true;
        stop();
        reject(new Error("Location request timed out. Please try again."));
      }
    };
    timer = setTimeout(onDeadline(false), LOCATION_SETTLE_MS);
  });

type OfficeGeofence = { lat: number; lon: number; radius: number; name?: string };

// Mirror of the server-side cap: how much of the reading's own uncertainty the
// geofence absorbs. A "you are here ± 120 m" fix really might be inside a 100 m
// office radius; rejecting it outright is what blocked legitimate punches.
const GEOFENCE_ACCURACY_SLACK_M = 150;

// Same set the AuthContext clears on logout — these are the keys the
// mobile login persists. Anything outside this list is unrelated app
// state (redux, theme, etc.) and shouldn't be in an attendance payload.
const ATTENDANCE_PAYLOAD_KEYS = [
  "accessToken",
  "refreshToken",
  "user",
  "level",
  "employee_id", 
  "branchID",
  "headOfficeID",
  "masterID",
  "fcmtoken",
  "workfromhome",
  "workingHoursStart1",
  "workingHoursEnd1",
  "maxDistance1",
  "officeLon1",
  "officeLat1",
  "workingHoursStart2",
  "workingHoursEnd2",
  "maxDistance2",
  "officeLon2",
  "officeLat2",
  "workingHoursStart3",
  "workingHoursEnd3",
  "maxDistance3",
  "officeLon3",
  "officeLat3",
];

// When the app runs inside Capacitor (mobile WebView), Preferences are
// mirrored to localStorage under the "CapacitorStorage." prefix. On a
// plain browser the same keys are written without the prefix. Read both.
const readStorageValue = (key: string): string | null => {
  // Web stores the per-login office config in sessionStorage now (per-tab
  // session); check that first, then fall back to localStorage and the
  // Capacitor-mirrored key for native.
  try {
    const session = sessionStorage.getItem(key);
    if (session !== null) return session;
  } catch {
    /* sessionStorage unavailable — fall through */
  }
  const direct = localStorage.getItem(key);
  if (direct !== null) return direct;
  return localStorage.getItem(`CapacitorStorage.${key}`);
};

// Read the up-to-three office geofences the login flow saves. Mirrors
// the Android GeofenceManager (officeLat1/Lon1/maxDistance1 … 3). When
// nothing is configured we return null and skip the client gate — the
// backend revalidates anyway.
const readStoredOffices = (): OfficeGeofence[] | null => {
  const offices: OfficeGeofence[] = [];
  for (let i = 1; i <= 3; i++) {
    const lat = parseFloat(readStorageValue(`officeLat${i}`) || "");
    const lon = parseFloat(readStorageValue(`officeLon${i}`) || "");
    const radius = parseFloat(readStorageValue(`maxDistance${i}`) || "");
    if (
      Number.isFinite(lat) &&
      Number.isFinite(lon) &&
      Number.isFinite(radius) &&
      (lat !== 0 || lon !== 0)
    ) {
      offices.push({ lat, lon, radius });
    }
  }
  return offices.length ? offices : null;
};

// The geofences to check against, straight from the server for the caller's
// CURRENT branch. Browser storage is only a mirror written at login: a tab that
// inherited its session, or one still holding an older org's keys, would gate
// against the wrong office entirely — the punch then failed with "location does
// not match" until the user logged out and back in. Storage stays as the
// offline fallback (and for native, where the geofence worker writes it).
const readConfiguredOffices = async (): Promise<OfficeGeofence[] | null> => {
  try {
    const res = await api.get("/getOfficeDetail");
    const rows: any[] = res.data?.offices || [];
    const offices: OfficeGeofence[] = [];
    for (const row of rows) {
      const lat = parseFloat(row?.lattitude);
      const lon = parseFloat(row?.longitude);
      const radius = parseFloat(row?.radius_meters);
      if (
        Number.isFinite(lat) &&
        Number.isFinite(lon) &&
        Number.isFinite(radius) &&
        radius > 0 &&
        (lat !== 0 || lon !== 0)
      ) {
        offices.push({ lat, lon, radius, name: row?.office_name || undefined });
      }
    }
    if (offices.length) return offices;
    // The branch genuinely has no geofence configured — don't fall back to a
    // stale copy that would block a punch the server would have accepted.
    if (Array.isArray(rows)) return null;
  } catch {
    /* offline / endpoint unreachable — fall back to the login-time mirror */
  }
  return readStoredOffices();
};

// Build the same key set mobile's clockOut() does, scoped to attendance
// keys only. Skip the literal string "undefined" that Capacitor writes
// when a value was saved as a JS `undefined`.
const buildAttendanceSnapshot = (): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const key of ATTENDANCE_PAYLOAD_KEYS) {
    const value = readStorageValue(key);
    if (value !== null && value !== "undefined") {
      out[key] = value;
    }
  }
  return out;
};

// Resolve the employee_id the backend requires at body root. Prefer the
// dedicated storage key (set on mobile login); fall back to the decoded
// JWT user object (which is what web login persists).
const resolveEmployeeId = (): string | null => {
  const direct = readStorageValue("employee_id");
  if (direct && direct !== "undefined") return direct;
  const userStr = readStorageValue("user");
  if (!userStr) return null;
  try {
    const u = JSON.parse(userStr);
    return (
      u?.employee_id ||
      u?.user_id ||
      u?.id ||
      u?.sub ||
      null
    );
  } catch {
    return null;
  }
};

export interface WebClockResult {
  ok: boolean;
  latitude: number;
  longitude: number;
  distance?: number; // meters to nearest office, when known
  raw: any;
  // Set when the punch was REFUSED because leaving before the shift ends needs
  // approval (attendance.early_exit_approval). Not an error: the way forward is
  // a request to the approver, not a retry — see EarlyExitPrompt.
  approvalRequired?: EarlyExitPrompt | null;
}

// Fetched once by the Attendance page on mount. The backend resolves it
// from Employee.modeOfWorking / Employee.workfromhome, so this is always
// fresh even when the JWT-derived localStorage values are stale.
export interface MyWorkMode {
  mode_of_working: string | null;
  workfromhome: boolean;
  // Overtime: live flag + the clock-button cutoff (ISO local datetime) =
  // next working day's office start - 30 min. null when not on overtime.
  is_overtime?: boolean;
  overtime_clock_until?: string | null;
  // Overtime button LOWER bound (ISO local) = work day's office end. The
  // button is hidden until now >= this. null when not on overtime.
  overtime_clock_from?: string | null;
  // The office hours that apply to this employee ("HH:MM"), resolved from their
  // own office_details row or the branch-wide one. office_end is what closes
  // the in-office Clock In button for the day; null means "no shift bound".
  office_start?: string | null;
  office_end?: string | null;
}

export const getMyWorkMode = async (): Promise<MyWorkMode> => {
  try {
    const res = await api.get<MyWorkMode>("/attendance/my-mode");
    return res.data;
  } catch {
    // Non-fatal — fall back to the existing localStorage heuristic.
    return {
      mode_of_working: null,
      workfromhome: false,
      is_overtime: false,
      overtime_clock_until: null,
      overtime_clock_from: null,
      office_start: null,
      office_end: null,
    };
  }
};

// ----- Overtime grants (employee card) -----
// Writes to the HRMS attendance backend, NOT the workmonitor socket service:
// allowing overtime is an attendance decision and must not start (or depend on)
// the desktop-monitoring agent.
export interface OvertimeGrant {
  employee_id: string;
  user_id?: string | null;
  is_overtime: boolean;
  overtime_start_date: string | null;
  overtime_end_date: string | null;
}

export const setEmployeeOvertime = async (payload: {
  employee_id: string;
  is_overtime: boolean;
  start_date?: string; // YYYY-MM-DD
  end_date?: string;   // YYYY-MM-DD
}): Promise<OvertimeGrant> => {
  try {
    const body: Record<string, any> = {
      employee_id: payload.employee_id,
      is_overtime: payload.is_overtime,
    };
    if (payload.is_overtime) {
      if (payload.start_date) body.start_date = payload.start_date;
      if (payload.end_date) body.end_date = payload.end_date;
    }
    const res = await api.post("/attendance/overtime", body);
    return res.data;
  } catch (err: any) {
    throw new Error(
      err?.response?.data?.detail || err?.response?.data?.error || err.message,
    );
  }
};

// Web-side Clock In / Clock Out. Throws with a user-friendly message
// when geolocation fails or the user is outside every configured
// geofence (and not flagged work-from-home).
export const sendWebClockEvent = async (
  clockIn: boolean,
  opts: { workfromhome?: boolean; overtime?: boolean; hybrid?: boolean } = {},
): Promise<WebClockResult> => {
  // Source of truth for "is this user remote / hybrid?":
  //
  //   1. If the caller passes `opts.workfromhome` explicitly (true OR
  //      false), trust it. The Attendance page sources this from
  //      /attendance/my-mode, which reads Employee.modeOfWorking live —
  //      so it's always fresher than anything in localStorage. Falling
  //      back to localStorage when the explicit value is `false` would
  //      let a stale `true` from a previous remote session override the
  //      current in-office state and silently skip the location prompt.
  //
  //   2. Only when `opts.workfromhome` is `undefined` do we read:
  //        a) Capacitor Preferences "workfromhome" (set on login)
  //        b) The persisted user object's workfromhome / modeOfWorking
  //      as a last-resort hint for callers that don't pass the flag.
  let workFromHome: boolean;

  if (typeof opts.workfromhome === "boolean") {
    workFromHome = opts.workfromhome;
  } else {
    const workFromHomeRaw = (readStorageValue("workfromhome") || "").toLowerCase();
    workFromHome = workFromHomeRaw === "true";

    if (!workFromHome && workFromHomeRaw !== "false") {
      try {
        const userStr = readStorageValue("user");
        if (userStr) {
          const u = JSON.parse(userStr);
          if (u?.workfromhome === true) workFromHome = true;
          const mode = (u?.modeOfWorking || u?.mode_of_working || "").toString().toLowerCase();
          if (mode === "remote" || mode === "hybrid") workFromHome = true;
        }
      } catch {
        /* ignore — fall back to the geofenced flow */
      }
    }
  }

  let latitude = 0;
  let longitude = 0;
  let accuracy: number | undefined;
  // Whether the 0/0 above was ever replaced by a real reading. Posting the
  // placeholder as a coordinate makes the server geofence against Null Island
  // (a point in the Atlantic) and log a ~6000km distance for what is simply
  // "no fix".
  let hasFix = false;
  let nearestDistance: number | undefined;

  // Overtime hours can be worked at the office OR at home, so the location is
  // still worth capturing — it decides whether the punch reads In Office or
  // WFH. What it must never do is BLOCK the punch. Hybrid behaves the same way
  // once a fix exists (off site simply means WFH), but a hybrid employee who
  // refuses location still gets an error rather than a silent WFH punch.
  const offSiteIsRemote = opts.overtime === true || opts.hybrid === true;

  if (!workFromHome) {
    try {
      const fix = await getBrowserCoordinates();
      latitude = fix.latitude;
      longitude = fix.longitude;
      accuracy = fix.accuracy;
      hasFix = true;
    } catch (err) {
      if (opts.overtime !== true) throw err;
      workFromHome = true;
    }
  }

  if (!workFromHome) {
    const offices = await readConfiguredOffices();
    if (offices) {
      // Absorb the reading's own uncertainty (capped): a fix that says "± 120 m"
      // may well be inside a 100 m radius, and rejecting it is what made the
      // first clock-in of a session fail. The server applies the same slack.
      const slack = Math.min(Math.max(accuracy ?? 0, 0), GEOFENCE_ACCURACY_SLACK_M);
      let withinAny = false;
      let nearest = Infinity;
      for (const o of offices) {
        const d = haversineMeters(latitude, longitude, o.lat, o.lon);
        if (d < nearest) nearest = d;
        if (d - slack <= o.radius) {
          withinAny = true;
          break;
        }
      }
      nearestDistance = Math.round(nearest);
      // A fix this coarse can't be judged here at all. The distance we just
      // computed is meaningless — the "position" came from an IP lookup or a
      // cell tower, not from measuring where this device is — and the office-IP
      // fallback that rescues wired desktops lives on the SERVER, the only side
      // that can see which network the punch arrives on. So send it and let the
      // server rule; it answers with the same explanation when the network
      // doesn't match either.
      const undecidableHere = accuracy != null && accuracy > COARSE_ACCURACY_M;
      if (!withinAny) {
        if (offSiteIsRemote) {
          workFromHome = true;
        } else if (!undecidableHere) {
          throw new Error(
            `You're about ${nearestDistance}m from the nearest office${
              accuracy ? ` (location accurate to ±${Math.round(accuracy)}m)` : ""
            }. Move within range to clock ${clockIn ? "in" : "out"}.`,
          );
        }
      }
    }
  }

  const employeeId = resolveEmployeeId();
  if (!employeeId) {
    throw new Error(
      "Couldn't identify your employee record. Please sign out and log in again.",
    );
  }

  // the backend pulls branch / office / config out of it. We mirror
  // that with a scoped snapshot (no unrelated localStorage), promote
  // employee_id to body root (the backend requires it), and serialise
  // the freshly read coordinates as strings (Pydantic schema expects
  // string lat/lng).
  const body = {
    ...buildAttendanceSnapshot(),
    employee_id: employeeId,
    clockIn,
    ...(hasFix
      ? { latitude: String(latitude), longitude: String(longitude) }
      : {}),
    // Let the server absorb the same uncertainty the client just did, so the
    // two geofence checks agree instead of one passing and the other failing.
    ...(accuracy != null ? { accuracy } : {}),
    // Make sure the backend sees the fresh WFH flag — the snapshot we
    // build above reads localStorage, which can be stale right after an
    // admin flips the employee to remote / hybrid. Explicit field wins.
    workfromhome: workFromHome,
    // Say which client we are rather than letting the server sniff the
    // User-Agent — a webview and a browser tab are hard to tell apart from
    // outside, and this is the field the attendance report is read from.
    attendance_mode: clientAttendanceMode(),
  };
  const res = await api.post("/attendance", body);
  const attendanceResult = res.data?.result;
  if (attendanceResult?.status === "error") {
    throw new Error(
      attendanceResult.message ||
      "Your current location does not match any configured office location.",
    );
  }
  return {
    ok: res.status >= 200 && res.status < 300,
    latitude,
    longitude,
    distance: nearestDistance,
    raw: res.data,
    approvalRequired:
      attendanceResult?.status === "approval_required"
        ? {
            message: attendanceResult.message as string,
            shift_end: attendanceResult.shift_end ?? null,
            early_by_minutes: attendanceResult.early_by_minutes ?? null,
            pending_request: attendanceResult.pending_request ?? null,
          }
        : null,
  };
};

// Send auto-clock attendance
export const sendAutoClockAttendance = async (employeeId: string) => {
  try {
    // Get current location
    const { coords } = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true
    });

    // Get Wi-Fi SSID if available
    let wifiSSID = null;
    try {
      const ssidResult = await (SSIDPlugin as any).getSSID();
      wifiSSID = ssidResult.ssid;
    } catch (e) {
      console.warn("Wi-Fi SSID not available", e);
    }

    // Send attendance to API
    const response = await api.post("/api/attendance/auto-clock", {
      employee_id: employeeId,
      latitude: coords.latitude,
      longitude: coords.longitude,
      wifi_ssid: wifiSSID
    });

    return response.data;
  } catch (err: any) {
    console.error("Error sending attendance", err);
    throw new Error(err?.response?.data?.error || err.message);
  }
};

// ----- Admin / HR / Manager attendance overrides -----
// Backend enforces role-based scoping: admin / hr head can target any
// employee in the branch, manager only their own reports. UI hides
// these calls for everyone else, but the server is the source of truth.

export const adminMarkPresent = async (payload: {
  user_id?: string;
  employee_id?: string;
  attendance_date?: string; // YYYY-MM-DD
  attendance_location?: "in_office" | "work_from_home";
}) => {
  try {
    const body: Record<string, any> = {};
    if (payload.employee_id) body.employee_id = payload.employee_id;
    if (payload.user_id) body.user_id = payload.user_id;
    if (payload.attendance_date) body.attendance_date = payload.attendance_date;
    if (payload.attendance_location) body.attendance_location = payload.attendance_location;
    const res = await api.post("/attendance/admin/mark_present", body);
    return res.data;
  } catch (err: any) {
    console.error("Error marking present:", err);
    throw new Error(err?.response?.data?.detail || err.message);
  }
};

// Mirror of adminMarkPresent — inserts an ABSENT row for the given
// employee on the given date.
export const adminMarkAbsent = async (payload: {
  user_id?: string;
  employee_id?: string;
  attendance_date?: string; // YYYY-MM-DD
}) => {
  try {
    const body: Record<string, any> = {};
    if (payload.employee_id) body.employee_id = payload.employee_id;
    if (payload.user_id) body.user_id = payload.user_id;
    if (payload.attendance_date) body.attendance_date = payload.attendance_date;
    const res = await api.post("/attendance/admin/mark_absent", body);
    return res.data;
  } catch (err: any) {
    console.error("Error marking absent:", err);
    throw new Error(err?.response?.data?.detail || err.message);
  }
};

export const adminUpdateWorkingHours = async (payload: {
  user_id?: string;
  employee_id?: string;
  attendance_date: string; // YYYY-MM-DD
  clock_in_time?: string;  // "HH:MM"
  clock_out_time?: string; // "HH:MM"
  attendance_location?: "in_office" | "work_from_home";
  // The exact punch being edited (session.record_id). Send it whenever the edit
  // came from a specific punch row — a day can hold several, and without it the
  // backend falls back to the day's most recent row and the change lands on the
  // wrong session.
  record_id?: string | null;
  add_new_punch?: boolean;
  clear_clock_out?: boolean;
  replace_day_record?: boolean;
}) => {
  try {
    const body: Record<string, any> = {
      attendance_date: payload.attendance_date,
    };
    if (payload.employee_id) body.employee_id = payload.employee_id;
    if (payload.user_id) body.user_id = payload.user_id;
    if (payload.record_id) body.record_id = payload.record_id;
    if (payload.add_new_punch) body.add_new_punch = true;
    if (payload.clear_clock_out) body.clear_clock_out = true;
    if (payload.replace_day_record) body.replace_day_record = true;
    if (payload.clock_in_time) body.clock_in_time = payload.clock_in_time;
    if (payload.clock_out_time) body.clock_out_time = payload.clock_out_time;
    if (payload.attendance_location) body.attendance_location = payload.attendance_location;
    const res = await api.post("/attendance/admin/update_working_hours", body);
    return res.data;
  } catch (err: any) {
    console.error("Error updating working hours:", err);
    throw new Error(err?.response?.data?.detail || err.message);
  }
};

export const adminDeleteAttendancePunch = async (payload: {
  user_id?: string;
  employee_id?: string;
  record_id: string;
}) => {
  try {
    const res = await api.post("/attendance/admin/delete_punch", payload);
    return res.data;
  } catch (err: any) {
    console.error("Error deleting attendance punch:", err);
    throw new Error(err?.response?.data?.detail || err.message);
  }
};

// Export attendance to CSV
export const exportAttendanceToCSV = (attendanceData: AttendanceStatus[]) => {
  const headers = ["Name", "In Time", "Out Time", "Status"];
  const rows = attendanceData.map((emp) => [
    emp.name,
    emp.clock_in_time || "-",
    emp.out_time || "-",
    emp.status || "-",
  ]);

  let csvContent = headers.join(",") + "\n" + rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  // Local date (not UTC) so the filename matches the user's "today".
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  link.setAttribute("download", `attendance_${today}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/* ---------------- EARLY CLOCK-OUT APPROVALS ----------------
 * Leaving before the shift ends is approval-based once a branch switches on
 * Settings -> Policy Rules -> "Early clock-out approval". The clock-out itself
 * is what discovers this (the punch comes back `approval_required`), so nothing
 * here is fetched speculatively: the dialog opens on refusal, and the banner
 * below the clock buttons is driven by today's own row.
 */
export type EarlyExitStatus = "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";

export interface EarlyExitRequestRow {
  id: string;
  employee_id: string | null;
  user_id: string;
  /** Only set on the lists, where the row is about somebody else. */
  employee_name: string | null;
  on_date: string | null;
  reason: string | null;
  shift_end: string | null;          // "HH:MM"
  early_by_minutes: number | null;
  early_by: string;                  // "1h 40m early"
  status: EarlyExitStatus;
  /** Raised by the server on the employee's behalf (allow & flag mode). */
  auto_raised: boolean;
  approver_user_id: string | null;
  approver_relation: string | null;
  approver_name: string | null;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  /** Stamped by the punch this approval authorised - one approval, one exit. */
  used_at: string | null;
  created_at: string | null;
}

/** What a refused clock-out tells the page. */
export interface EarlyExitPrompt {
  message: string;
  shift_end: string | null;
  early_by_minutes: number | null;
  pending_request: EarlyExitRequestRow | null;
}

export interface EarlyExitRequestResult {
  // not_required / already_approved are NOT failures: by the time the reason
  // was typed the shift may have ended, or the approval may already be in.
  status: "pending" | "already_pending" | "already_approved" | "not_required";
  message: string;
  approver_count?: number;
  request: EarlyExitRequestRow | null;
}

export const requestEarlyExit = async (
  reason: string,
): Promise<EarlyExitRequestResult> => {
  try {
    const res = await api.post<EarlyExitRequestResult>(
      "/attendance/early_exit_requests",
      { reason },
    );
    return res.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.detail?.[0]?.msg ||
      error?.response?.data?.detail ||
      error.message,
    );
  }
};

export const listEarlyExitRequests = async (opts: {
  scope: "mine" | "approvals";
  status?: EarlyExitStatus;
  onDate?: string;   // YYYY-MM-DD
  days?: number;
} = { scope: "mine" }): Promise<{ requests: EarlyExitRequestRow[]; pending_count: number }> => {
  const res = await api.get("/attendance/early_exit_requests", {
    params: {
      scope: opts.scope,
      ...(opts.status ? { status: opts.status } : {}),
      ...(opts.onDate ? { on_date: opts.onDate } : {}),
      ...(opts.days ? { days: opts.days } : {}),
    },
  });
  return { requests: res.data?.requests || [], pending_count: res.data?.pending_count || 0 };
};

export const decideEarlyExitRequest = async (
  id: string,
  decision: "approve" | "reject",
  note?: string,
): Promise<EarlyExitRequestRow> => {
  try {
    const res = await api.post(`/attendance/early_exit_requests/${id}/decide`, {
      decision,
      note: note || null,
    });
    return res.data?.request;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.detail || error.message,
    );
  }
};

export const cancelEarlyExitRequest = async (
  id: string,
): Promise<EarlyExitRequestRow> => {
  try {
    const res = await api.post(`/attendance/early_exit_requests/${id}/cancel`);
    return res.data?.request;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

/* ---------------- NOTIFICATION BELL FEED ----------------
 * Late-coming warnings: the scheduler sends an FCM push AND persists a bell
 * row, so the warning is still waiting in-app if the push was muted or missed.
 * Same reminder-feed contract as the leave / expense / asset feeds.
 */
export interface AttendanceNotification {
  id: string;
  kind: string; // LATE_WARNING | EARLY_EXIT_REQUEST | EARLY_EXIT_DECISION
  source: "attendance";
  on_date: string | null;
  task_id: string; // "attendance:<date>"
  title: string;
  body: string | null;
  is_read: boolean;
  created_at: string;
}

export const getAttendanceNotifications = (limit = 50) =>
  api.get<{ total: number; items: AttendanceNotification[] }>(
    `/attendance_notifications/unread?limit=${limit}`
  );

export const markAttendanceNotificationRead = (id: string) =>
  api.post(`/attendance_notifications/${id}/read`);

// ---------------------------------------------------------------------------
// "Where do I stand this month?" — late coming + the 221-hour minimum.
//
// Comes from the SAME payroll calculation the payslip is built from, so what
// this shows is what will actually be charged. The policy announcement asks
// people to track their hours "so that any shortfall can be covered in time";
// this is the data behind that sentence.
// ---------------------------------------------------------------------------
export interface PolicyStatus {
  year: number;
  month: number;
  as_of: string;
  is_current_month: boolean;
  late: {
    count: number;
    free_days: number;
    free_left: number;
    dates: string[];
    charged_days: number;
    amount: number;
    action: 'hold' | 'deduct' | 'none';
    late_after: string | null;
    amount_mode: string | null;
    // Held by EARLIER months and not yet released — separate from `amount`,
    // which is this month alone. Answers "where is last month's money?".
    held_earlier: {
      amount: number;
      months: string[];          // e.g. ["2026-08"]
      release_if_lates_lte: number | null;
    };
    // A projection: what ONE more late day would cost from here.
    next_day_cost: { amount: number; total_after: number; action: string };
  };
  hours: {
    enabled: boolean;
    required: number;
    worked: number;
    shortfall: number;
    expected_by_now: number;
    pace: number;          // + ahead of where you need to be, − behind
    on_track: boolean;
    // What the shortfall would cost IF THE MONTH ENDED TODAY. Mid-month this is
    // always the worst case — the remaining days can still make it up.
    projected_deduction: number;
    hourly_rate: number;
  };
}

export const getMyPolicyStatus = async (
  year?: number,
  month?: number,
  employeeId?: string,
): Promise<PolicyStatus> => {
  try {
    const response = await api.get(`/attendance/my_policy_status`, {
      params: { year, month, employee_id: employeeId },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching policy status:', error);
    throw new Error(
      error?.response?.data?.detail || error?.response?.data?.error || error.message,
    );
  }
};
